import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { LOOKUP_CODES } from '@core/lookup/lookup-codes';
import { LookupStore } from '@core/lookup/lookup.store';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { toNullableDateString } from '@shared/utils/date-utils';
import { SERVICE_CLOSURE_OUTCOME_CODES } from '../../../intervention-closure';
import { stageLabel } from '../../../intervention-stage';
import { toUpdateRequest } from '../../../intervention-update';
import { InterventionResponse, InterventionTypeResponse, UpdateInterventionRequest } from '../../../intervention.model';

// Moves a case to its next stage by hand, or closes it. The stages follow from the work (eligibility, the assessment, the referral, the reports);
// this is for the moves that do not: the last step of a type with no approval stage, and first call resolution straight from Assessment.
@Component({
  selector: 'intervention-stage-panel',
  imports: [ReactiveFormsModule, ...SharedMaterialUIModule],
  templateUrl: './intervention-stage-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class InterventionStagePanelComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly lookupStore = inject(LookupStore);

  readonly intervention = input.required<InterventionResponse>();
  readonly lifecycleStages = input<string[]>([]);
  readonly interventionTypes = input<InterventionTypeResponse[]>([]);
  // The case status on the latest close-out report: offered as the closure outcome when closing, so it is not retyped.
  readonly suggestedClosureOutcomeId = input<string | null>(null);
  readonly busy = input(false);
  // A user who cannot update the case sees where it stands, not the move.
  readonly readOnly = input(false);
  readonly submitted = output<UpdateInterventionRequest>();

  protected readonly stageLabel = stageLabel;

  // The stage chosen to move to (a signal, so the closing fields follow it).
  protected readonly chosenStage = signal('');

  protected readonly form = this.formBuilder.group({
    closureDate: [null as Date | string | null],
    closureReason: [''],
    closureOutcomeId: [''],
  });

  // The stages the case can be moved to from where it is: the next one, and Closed for a first call resolution straight from Assessment.
  protected readonly moves = computed<string[]>(() => {
    const { status, interventionTypeId } = this.intervention();
    const stages = this.lifecycleStages();
    const next = stages[stages.indexOf(status) + 1];
    const moves = next && stages.includes(status) ? [next] : [];

    const type = this.interventionTypes().find((item) => item.id === interventionTypeId);
    if (status === 'Assessment' && type?.fcrAvailable && stages.includes('Closed') && !moves.includes('Closed')) {
      moves.push('Closed');
    }

    return moves;
  });

  // Closing from service delivery onwards has to name an outcome, and only these can be chosen: first call resolution and referral provided come
  // from the path taken. The server enforces the rest of the closure rules (attendance must be verified unless the outcome means no service was completed).
  protected readonly serviceClosureOutcomes = computed(() =>
    this.lookupStore
      .items(LOOKUP_CODES.closureOutcome)
      .filter((item) => SERVICE_CLOSURE_OUTCOME_CODES.includes(item.code ?? '')),
  );

  protected readonly closing = computed(() => this.chosenStage() === 'Closed');

  protected readonly isServiceClosure = computed(
    () => this.closing() && ['ServiceDelivery', 'Reporting', 'Approval'].includes(this.intervention().status),
  );

  constructor() {
    this.lookupStore.load([LOOKUP_CODES.closureOutcome]);

    // Once the case has moved, the stage that was chosen is not one of the moves any more.
    effect(() => {
      const moves = this.moves();
      untracked(() => {
        if (!moves.includes(this.chosenStage())) {
          this.chosenStage.set('');
        }
      });
    });
  }

  protected stageChosen(stage: string): void {
    this.chosenStage.set(stage);

    const suggestion = this.suggestedClosureOutcomeId();
    const control = this.form.controls.closureOutcomeId;
    if (this.isServiceClosure() && suggestion && !control.value && this.serviceClosureOutcomes().some((item) => item.id === suggestion)) {
      control.setValue(suggestion);
    }
  }

  protected submit(): void {
    if (!this.chosenStage()) {
      return;
    }

    const control = this.form.controls.closureOutcomeId;
    if (this.isServiceClosure() && !control.value) {
      control.setErrors({ required: true });
      control.markAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    this.submitted.emit(
      toUpdateRequest(this.intervention(), {
        status: this.chosenStage(),
        closureDate: this.closing() ? toNullableDateString(value.closureDate) : null,
        closureReason: this.closing() ? value.closureReason?.trim() || null : null,
        closureOutcomeId: this.closing() ? value.closureOutcomeId || null : null,
      }),
    );
  }
}
